import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';

import { SEO_DEFAULTS } from '../../../shared/constants/seo.constants';
import type {
  BlogCategory,
  BlogGameRef,
  BlogPost,
} from '../../../shared/models/blog.model';
import type { SeoInput, SeoRobots, SeoService } from '../../../shared/services/seo.service';
import type { BreadcrumbItem } from '../../../shared/ui/breadcrumb/breadcrumb.component';
import {
  absoluteUrl,
  buildBreadcrumbJsonLd,
  type BreadcrumbCrumb,
} from '../../../shared/utils/seo.utils';
import { BLOG_COPY } from '../constants/blog.constants';
import { BlogTransferredError } from '../data/blog.service';
import type { BlogAppliedFilters, BlogListFilters } from '../data/blog-view.model';

/** Ruta del listado; es también el canonical de todo lo no indexable (B66). */
export const BLOG_LIST_PATH = '/blog';

/**
 * Robots de las URL del listado que no se indexan pero cuyos enlaces sí se
 * siguen (B66), como `/buscar`. `SeoRobots` no recoge esta variante —`/buscar`
 * la emite por `data.seo`, que no está tipado— y `SeoService` no se toca en el
 * blog, así que se estrecha aquí a mano (P22 del spec: ampliar el tipo).
 */
const NOINDEX_FOLLOW = 'noindex,follow' as string as SeoRobots;

/** Ruta pública de un artículo; es también su canonical. */
export function blogArticlePath(slug: string): string {
  return `/blog/${slug}`;
}

/**
 * SEO de un artículo (B61): OG `article`, canonical propio, imagen para
 * compartir y JSON-LD `BlogPosting`. Título, descripción e imagen salen de
 * `meta_title`, `meta_description` y `og_image_url`, que la API ya rellena con
 * título, extracto y portada cuando el admin los deja vacíos (B12, B13); el
 * respaldo local es por si faltaran. `SeoService` hace absolutas la imagen y
 * el canonical.
 */
export function buildBlogArticleSeo(post: BlogPost): SeoInput {
  const canonical = blogArticlePath(post.slug);
  const image = encodeImagePath(post.ogImageUrl || post.imageUrl);
  return {
    title: post.metaTitle || post.title,
    description: post.metaDescription || post.excerpt,
    image,
    type: 'article',
    canonical,
    jsonLd: [
      buildBlogPostingJsonLd(post, canonical, image),
      buildBreadcrumbJsonLd(toJsonLdCrumbs(blogArticleBreadcrumbs(post))),
    ],
  };
}

/** Un slug que no existe no debe indexarse (B62, patrón de `game-section`). */
export function buildBlogArticleNotFoundSeo(): SeoInput {
  return { title: BLOG_COPY.notFoundTitle, robots: 'noindex,nofollow' };
}

/**
 * La API no respondió (B123): ni se indexa ni se da canonical ni JSON-LD, para
 * que un fallo pasajero no sustituya en los buscadores a la página buena.
 */
export function buildBlogLoadErrorSeo(): SeoInput {
  return { title: BLOG_COPY.seoTitle, robots: 'noindex,nofollow' };
}

/**
 * Aplica el SEO de una página del blog sin dejar que un fallo suyo se confunda
 * con una caída de la API (Astra W5, A1). Un error al construir o aplicar los
 * tags no es un 503: se registra y la página se pinta igual, con el estado HTTP
 * que le toque (200 o 404). Propagarlo rompería la navegación y en SSR
 * acabaría en el «Cannot GET» de Express.
 */
export function applyBlogSeoSafely(seo: Pick<SeoService, 'apply'>, build: () => SeoInput): void {
  try {
    seo.apply(build());
  } catch (err) {
    console.error('[blog] SEO', err);
  }
}

/** Segundos que se piden al rastreador antes de reintentar un 503 (B132). */
export const BLOG_RETRY_AFTER_SECONDS = 300;

/**
 * Estado HTTP real de la respuesta SSR (B122, B132). `RESPONSE_INIT` solo
 * existe en el servidor con `RenderMode.Server`; en el navegador llega `null`
 * y no se hace nada. `@angular/ssr` lee el objeto cuando ya han resuelto las
 * rutas (`ssr.mjs:2206`), así que basta con mutarlo aquí.
 */
export function setBlogResponseStatus(init: ResponseInit | null, status: 301 | 404): void {
  if (init) init.status = status;
}

/** 503 + `Retry-After` para el estado de error (B132 a). */
export function setBlogUnavailableStatus(init: ResponseInit | null): void {
  if (!init) return;
  init.status = 503;
  const headers = new Headers(init.headers);
  headers.set('Retry-After', String(BLOG_RETRY_AFTER_SECONDS));
  init.headers = headers;
}

/**
 * ¿El fallo es de la FUENTE (la API)? Respuesta HTTP de error —incluido el
 * status 0 de «sin conexión»—, `TimeoutError` de rxjs, o el fallo que el SSR
 * transfirió al navegador (B121). Cualquier otra cosa (un `TypeError` del
 * mapper tras un 200, por ejemplo) es un error nuestro (Astra W5, B2).
 */
export function isBlogSourceError(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse ||
    err instanceof TimeoutError ||
    err instanceof BlogTransferredError
  );
}

/**
 * Estado HTTP del estado de error según quién falló (Astra W5, B2). La UI es
 * la misma (`BLOG_LOAD_ERROR`): lo que cambia es lo que se le dice al
 * rastreador. Fallo de fuente → 503 + `Retry-After` (vuelve luego). Error
 * interno → 500 sin `Retry-After` (reintentar no lo arregla), y se registra.
 */
export function setBlogLoadFailureStatus(init: ResponseInit | null, err: unknown): void {
  if (isBlogSourceError(err)) {
    setBlogUnavailableStatus(init);
    return;
  }
  console.error('[blog] error interno', err);
  if (init) init.status = 500;
}

/**
 * `publisher` es la misma `Organization` que declara `src/index.html`. Ese
 * nodo no tiene `@id`, así que no se puede referenciar: se incrusta con los
 * mismos `name`/`url`/`logo`, construidos desde `SEO_DEFAULTS` como hace
 * `buildOrganizationJsonLd()`.
 */
export function buildBlogPostingJsonLd(
  post: BlogPost,
  canonical: string,
  image: string,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: absoluteUrl(image),
    datePublished: post.publishedAt,
    author: { '@type': 'Person', name: post.authorName },
    publisher: {
      '@type': 'Organization',
      name: SEO_DEFAULTS.siteName,
      url: SEO_DEFAULTS.siteUrl,
      logo: absoluteUrl('/images/logo/logo.png'),
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(canonical) },
  };
}

/**
 * `absoluteUrl()` no codifica: una ruta de imagen con espacios o tildes
 * saldría literal en `og:image`. Una ruta que ya trae escapes `%XX` se respeta
 * para no codificarla dos veces.
 */
function encodeImagePath(path: string): string {
  return /%[0-9A-F]{2}/i.test(path) ? path : encodeURI(path);
}

// ── Listado (B66, B67) ─────────────────────────────────────────────────────

/**
 * Qué listado es, a efectos de SEO y migas (B66). Solo son indexables el
 * listado sin filtros y el de UN solo juego o UNA sola categoría que exista;
 * `null` = cualquier otra cosa (búsqueda, etiqueta, combinaciones o un slug
 * que el servidor no ha resuelto).
 */
export type BlogListing =
  | { kind: 'all' }
  | { kind: 'game'; game: BlogGameRef }
  | { kind: 'category'; category: BlogCategory }
  | null;

export function classifyBlogListing(
  filters: BlogListFilters,
  applied: BlogAppliedFilters,
): BlogListing {
  const count = activeFilterCount(filters);
  if (count === 0) return { kind: 'all' };
  if (count > 1) return null;
  if (filters.game && applied.game) return { kind: 'game', game: applied.game };
  if (filters.category && applied.category) {
    return { kind: 'category', category: applied.category };
  }
  return null;
}

/**
 * SEO del listado (B66). La página no cambia la clasificación ni entra en el
 * canonical, pero `page ≥ 2` nunca se indexa y apunta a la página 1 de ese
 * mismo listado.
 */
export function buildBlogListSeo(
  filters: BlogListFilters,
  applied: BlogAppliedFilters,
  page: number,
): SeoInput {
  const listing = classifyBlogListing(filters, applied);
  const head = listingHead(listing, filters, applied);
  return {
    ...head,
    type: 'website',
    robots: listing && page < 2 ? 'index,follow' : NOINDEX_FOLLOW,
    jsonLd: buildBreadcrumbJsonLd(toJsonLdCrumbs(blogListBreadcrumbs(filters, applied))),
  };
}

/** Migas del listado: el nombre de la taxonomía solo en un listado indexable. */
export function blogListBreadcrumbs(
  filters: BlogListFilters,
  applied: BlogAppliedFilters,
): BreadcrumbItem[] {
  const listing = classifyBlogListing(filters, applied);
  const home: BreadcrumbItem = { label: BLOG_COPY.breadcrumbHome, route: '/' };
  if (listing?.kind === 'game' || listing?.kind === 'category') {
    const name = listing.kind === 'game' ? listing.game.name : listing.category.name;
    return [
      home,
      { label: BLOG_COPY.breadcrumbBlog, route: BLOG_LIST_PATH },
      { label: name, route: null },
    ];
  }
  return [home, { label: BLOG_COPY.breadcrumbBlog, route: null }];
}

/**
 * Migas de la ficha (B67): Inicio › Blog › {juego} › {título}. El juego es
 * único y obligatorio en el post (B29) y lleva al listado de ese juego.
 */
export function blogArticleBreadcrumbs(post: BlogPost): BreadcrumbItem[] {
  return [
    { label: BLOG_COPY.breadcrumbHome, route: '/' },
    { label: BLOG_COPY.breadcrumbBlog, route: BLOG_LIST_PATH },
    { label: post.game.name, route: BLOG_LIST_PATH, queryParams: { game: post.game.slug } },
    { label: post.title, route: null },
  ];
}

/**
 * `buildBreadcrumbJsonLd` recibe la ruta como texto: aquí se le pega la query
 * que la miga visual pasa aparte en `queryParams`.
 */
export function toJsonLdCrumbs(items: readonly BreadcrumbItem[]): BreadcrumbCrumb[] {
  return items.map(({ label, route, queryParams }) => ({
    label,
    route: route && queryParams ? withQuery(route, queryParams) : route,
  }));
}

function listingHead(
  listing: BlogListing,
  filters: BlogListFilters,
  applied: BlogAppliedFilters,
): Pick<SeoInput, 'title' | 'description' | 'canonical'> {
  switch (listing?.kind) {
    case 'game':
      return {
        title: BLOG_COPY.seoTitleGame(listing.game.name),
        description: BLOG_COPY.seoDescriptionGame(listing.game.name),
        canonical: withQuery(BLOG_LIST_PATH, { game: listing.game.slug }),
      };
    case 'category':
      return {
        title: BLOG_COPY.seoTitleTaxonomy(listing.category.name),
        description: BLOG_COPY.seoDescriptionCategory(listing.category.name),
        canonical: withQuery(BLOG_LIST_PATH, { category: listing.category.slug }),
      };
    default: {
      // Una etiqueta sola conserva su nombre en el título aunque no se indexe.
      const tagOnly = activeFilterCount(filters) === 1 && filters.tag ? applied.tag : undefined;
      return {
        title: tagOnly ? BLOG_COPY.seoTitleTaxonomy(tagOnly.name) : BLOG_COPY.seoTitle,
        description: BLOG_COPY.seoDescription,
        canonical: BLOG_LIST_PATH,
      };
    }
  }
}

function activeFilterCount(filters: BlogListFilters): number {
  return [filters.game, filters.category, filters.tag, filters.search?.trim()].filter(Boolean)
    .length;
}

function withQuery(path: string, params: Record<string, string>): string {
  const query = new URLSearchParams(params).toString();
  return query ? `${path}?${query}` : path;
}
